import { IShareContentRecipient, ShareDialog, ShareDialogProps } from '@tableau/share-dialog';
import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import React from 'react';
import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { ModalInstance, ReactModalOptions, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { IToastOptions, ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ShareDialogUserActionOptions, ShareDialogUserActionsEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { EffectivePermissions } from '~permissions/requests/EffectivePermissions';
import { IGranteePermissions } from '~permissions/types/IGranteePermissions';
import { IPermission,Permission } from '~permissions/types/Permission';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { CollectionDefinition } from '~src/Collections/contentDefinitions/CollectionDefinition';
import { Set } from '~src/commonInfra/utils/Set';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { ITypedId, ITypedIdWithName } from '~src/httpClient/serverApi/types/ITypedId';
import { isMetricsEnabled } from '~src/metrics/util/MetricUtils';
import { ExplicitPermissionsResult, getExplicitPermissionsForAuthorizables } from '~src/permissions/requests/ExplicitPermissions';
import { GetActionableObjectsForAction } from '~src/permissions/requests/GetActionableObjectsForAction';
import { getAuthorizableType } from '~src/permissions/types/AuthorizableType';
import { PermissionsEditabilityLogic } from '~src/permissions/utils/PermissionsEditabilityLogic';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import {
  collectionShareUrl,
  datasourceShareUrl,
  flowShareUrl,
  metricEmbedSnippet,
  metricShareUrl,
  projectShareUrl,
  publishedConnectionShareUrl,
  ShareOrigin,
  viewShareUrl,
  workbookShareUrl,
} from '~src/sharing/services/ShareUrls';
import { userCanManageAnyCollection } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityActionHelpers';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { IExplorableItemTypeInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { getDataConnectionId } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';
import { ISiteUser } from '~src/userManagement/users/serverApi/types/ISiteUser';
import { IUser } from '~src/userManagement/users/serverApi/types/IUser';
import { IViewDetailInfo } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { ShareContentAction } from '../actions/ShareContentAction';
import { GrantPermissionsAfterSharingDialog } from '../components/namedSharing/GrantPermissionsAfterSharingDialog';

const shareDialogTestId = 'share-dialog';

/*
* getBaseDialogOptions() - constant properties for share dialog which are reused in tests
* Why it's method: https://tableau.slack.com/archives/C9H9ERB5G/p1553880792040700
*/
const getBaseDialogOptions = () => {
  return {
    testId: shareDialogTestId,
    type: DialogType.NormalWithTitleBar,
    dialogWidth: 500,
    showCloseButton: true,
    positioningBehavior: DialogPositioningBehavior.Draggable,
    shouldReturnFocusOnClose: true,
  };
};

const popToast = (toast: IToastOptions): void => {
  const service = ToasterService.instance;
  service.pop(toast);
};

/**
 * Callback method to show toast in VizportalClient.
 * @param message toast message text sent from share-dialog
 */
const toastHandler = (message: string, isInfo: boolean): void => {
  popToast({
    text: message,
    type: isInfo ? ToastType.Success : ToastType.Error,
  });
};

const logEvent = (event: ShareDialogUserActionsEvent): void => {
  Telemetry.sendEvent<ShareDialogUserActionsEvent>(
    {
      type: event.type,
      UserActionType: event.UserActionType,
      Args: event.Args
    }
  );
};

/**
 * Wrapper function for telemetry callback. Share dialog calls this function, to log user
 * actions to PUT database, when user clicks on copy link, share button etc
 * @param userActionType One of the user action in ShareDialogUserActionOptions
 * @param args (optional) params
 */
const telemetryLogger = (userActionType: ShareDialogUserActionOptions, args?: string): void => {
  logEvent({ type: TelemetryEvents.ShareDialogUserActions, UserActionType: userActionType, Args: args });
};

let modalInstance: ModalInstance;
/**
 * Callback method to close dialog in VizportalClient.
 */
type CloseDialogHandlerType = (shareRecipients: IShareContentRecipient[]) => void;
const createCloseDialogHandler = (content: IContentInfo<ExplorableItemType>): CloseDialogHandlerType => {
  return async (shareRecipients: IShareContentRecipient[]) => {
    // modalInstance is null only in unit tests
    modalInstance && modalInstance.close();
    telemetryLogger(ShareDialogUserActionOptions.CloseShareDialog);

    if (FeatureFlags.grantPermissionsAfterSharing) {
      const permissionResult = await canSharerGrantPermission(content);
      if (permissionResult.canGrantPermissions) {
        const usersWithoutReadPermissions: (IUser | ISiteUser)[] = await getUsersWithoutReadPermissionsForContent(shareRecipients, content);
        // if some users don't have permissions, ask if we want to give them permissions
        if (usersWithoutReadPermissions.length > 0) {
          const unpermissionedRecipients: ITypedId[] = usersWithoutReadPermissions.map(user => {
            return { type: 'user', id: user.id };
          });
          GrantPermissionsAfterSharingDialog.open({ content, shareRecipients, unpermissionedRecipients, sourceAuthorizable: permissionResult.sourceAuthorizable, isRequestAccessDialogEnabled: false});
        }
      }
    } else if (content.contentType === CollectionDefinition.contentType &&
        userCanManageAnyCollection(getAppStore().getState(), [content])) {

      const usersWithoutReadPermissions: (IUser | ISiteUser)[] = await getUsersWithoutReadPermissionsForContent(shareRecipients, content);

      // if some users don't have permissions, ask if we want to give them permissions
      if (usersWithoutReadPermissions.length > 0) {
        const unpermissionedRecipients: ITypedId[] = usersWithoutReadPermissions.map(user => {
          return { type: 'user', id: user.id };
        });
        GrantPermissionsAfterSharingDialog.open({ content, shareRecipients, unpermissionedRecipients, isRequestAccessDialogEnabled: false });
      }
    }
  };
};

type CanGrantPermissionType = {
  canGrantPermissions: boolean;
  /* only included if the content does not control its own permissions */
  sourceAuthorizable?: ITypedIdWithName;
}

async function canSharerGrantPermission(content: IContentInfo<ExplorableItemType>): Promise<CanGrantPermissionType> {
  const authorizableType = getAuthorizableType(content.contentType);
  const explicitPermissions: ExplicitPermissionsResult = await getExplicitPermissionsForAuthorizables({type: authorizableType, ids: [content.id]}, false);
  if (explicitPermissions.errors && explicitPermissions.errors.length) {
    // if the user cannot get explicit permissions, then they are not allowed to set permissions
    return {canGrantPermissions: false};
  }
  const authorizable = Set.fromIdItemArray([content]);
  const isEditable: boolean = PermissionsEditabilityLogic.getPermissionsEditability(authorizable, authorizableType, explicitPermissions.sourceAuthorizablesById).editable;
  if (isEditable) {
    // if the user can get explicit permissions AND the permissions are editable, it means that they can set permissions
    return {canGrantPermissions: true};
  }
  const controllingContent: ITypedIdWithName = explicitPermissions.sourceAuthorizablesById[content.id];
  const actionableItems = await GetActionableObjectsForAction.editPermissions({type: controllingContent.type, ids: [controllingContent.id]});

  if (actionableItems.ids.includes(controllingContent.id)) {
    return {canGrantPermissions: true, sourceAuthorizable: controllingContent};
  }
  return {canGrantPermissions: false};
}

async function getUsersWithoutReadPermissionsForContent(shareRecipients: IShareContentRecipient[], content: IContentInfo<ExplorableItemType>) {
  const userIds: string[] = shareRecipients.map(recipient => recipient.recipientId.toString());

  const effectivePermissions: ListResult<IGranteePermissions> = await EffectivePermissions.getEffectivePermissionsWithoutGettingSiteUsers(
    userIds,
    { type: content.contentType, ids: [content.id] }
  );

  const usersWithoutReadPermissions: (IUser | ISiteUser)[] = [];
  for (let perms of effectivePermissions.items) {
    const permissionsForContent: _.Dictionary<IPermission> = perms.permissionsByContentType[content.contentType];
    if (permissionsForContent?.read?.permission !== Permission.Allowed) {
      usersWithoutReadPermissions.push(perms.user);
    }
  }

  return usersWithoutReadPermissions;
}

function* handleShareContentAction(action: ShareContentAction) {
  try {
    const content = action.content;

    const siteUrls = yield select(siteUrlsSelector);
    const isNamedSharingEnabled = yield select(SessionSiteSelectors.isNamedSharingEnabled);
    let shareLink: string;
    let embedLink: string;
    let title: string;
    let contentId = content.id;


    switch (content.contentType) {
      case 'view':
      case 'recommendedView':
        const viewDetailInfo: IViewDetailInfo = yield call(getViewDetailInfo, content);
        shareLink = yield call(viewShareUrl, siteUrls, viewDetailInfo.path, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForView();
        break;
      case 'project':
        shareLink = yield call(projectShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForProject();
        break;
      case 'workbook':
        shareLink = yield call(workbookShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForWorkbook();
        break;
      case 'datasource':
        // Datasource has two ids - datasource id and data connection id. For sharing to work properly,
        // the datasource id should be passed as contentId and the data connection id should be used to
        // generate shareLink. The fix for DEFECT 988384 tried to address this issue, but because Explore
        // page and Project page uses different APIs to fetch content, there are two different cases of
        // content passed to ShareContentAction:
        //
        // 1. content = {
        //    id = datasource id
        // }
        //
        // 2. content = {
        //    idForFavoriting = datasource id,
        //    id = data connection id
        // }
        //
        // The second case works. To fix DEFECT 1120191, in the first case, extra API call is required.
        const idForFavoriting = (content as IDataSourceInfo).idForFavoriting;
        if (!idForFavoriting) {
          const dataConnectionId = yield call(getDataConnectionId, content.id);
          contentId = content.id;
          shareLink = yield call(datasourceShareUrl, siteUrls, dataConnectionId, ShareOrigin.CardLink);
        } else {
          contentId = idForFavoriting;
          shareLink = yield call(datasourceShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        }
        title = Messages.shareDialogTitleForDatasource();
        break;
      case 'flow':
        shareLink = yield call(flowShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForFlow();
        break;
      case 'metric':
        shareLink = yield call(metricShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForMetric();
        if (FeatureFlags.metricsEmbeddingEnabled && isMetricsEnabled()) {
          embedLink = yield call(metricEmbedSnippet, siteUrls, content.id);
        }
        break;
      case 'virtualConnection':
        shareLink = yield call(publishedConnectionShareUrl, siteUrls, content.id, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForPublishedConnection();
        break;
      case 'collection':
        shareLink = yield call(collectionShareUrl, siteUrls, content.luid, ShareOrigin.CardLink);
        title = Messages.shareDialogTitleForCollection();
        break;
      default:
        return;
    }

    const shareDialogProps: ShareDialogProps = {
      closeDialogHandler: createCloseDialogHandler(content),
      contentId,
      contentName: content.name,
      contentType: content.contentType,
      encodedShareLink: shareLink,
      isNamedSharingEnabled,
      toastHandler: toastHandler,
      shouldShareThumbnail: true, // When sharing from VizPortal, we always want to share the thumbnail for a view, not a VizSnapshot
      logEvent: telemetryLogger,
      embedCode: embedLink, // Optional prop, undefined for all except metrics when metricsEmbeddingEnabled flag is true
      origin: ShareOrigin.CardLink,
      siteLuid: getSessionManagementServiceInstance().sessionSiteLuid()
    };

    const dialogOptions: ReactModalOptions = {
      ...getBaseDialogOptions(),
      title,
      dialogContent: React.createElement(ShareDialog, shareDialogProps)
    };

    modalInstance = yield call(ReactModalService.open, dialogOptions);

    // Log for what entity did we open the share dialog
    yield call(telemetryLogger, ShareDialogUserActionOptions.OpenShareDialog, content.contentType);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getViewDetailInfo(content: IExplorableItemTypeInfo): Promise<IViewDetailInfo> {
  return Views.getById(content.id);
}

export { createCloseDialogHandler, getBaseDialogOptions, handleShareContentAction, telemetryLogger, toastHandler };
